import React from 'react';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, IconWrapper, Row, Typography } from 'components';
import { EditIconButton } from 'components/common/icons';
import { PERMISSIONS } from 'constants/permissions';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';
import { RolesDrawer } from 'pages/admin/users/rolesDrawer';
import { UserStatus } from 'components/common/userStatus';
import { USER_TYPES, UserTypesMapping } from '../../../../../constants';

export const Details = React.memo(() => {
  const { onUpdateClick, userDetails, onExpand, drawerState, onResendInvitation, isLoading } = useDetails();
  return (
    <>
      <Row gutter={[0, 16]}>
        <Col span={24} className={styles.main_info}>
          <Row justify={'space-between'} wrap={false}>
            <Col span={20}>
              <Row gutter={[0, 4]}>
                <Col span={24} className={styles.id_container}>
                  <Typography.Text className={'font-size-12'} style={{ color: '#525866' }}>
                    ID: {userDetails.uuid}
                  </Typography.Text>
                </Col>
                <Col span={24} className={styles.legal_name}>
                  <Typography.Text title={userDetails.firstName + ' ' + userDetails.lastName} className={styles.name}>
                    {userDetails.firstName} {userDetails.lastName}
                  </Typography.Text>
                </Col>
                <Col span={24}>
                  <Row>
                    <Typography.Text style={{ color: '#99A0AE' }}>{userDetails.email}</Typography.Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} align={'middle'}>
                    <Col>
                      <UserStatus status={userDetails.status} />
                    </Col>
                    {userDetails.status === 'Invited' && (
                      <Col>
                        <HasAccess role={PERMISSIONS.users.user.write}>
                          <Button
                            loading={isLoading}
                            onClick={onResendInvitation}
                            size={'small'}
                            type={'link'}
                            variant={'link'}
                            style={{
                              color: '#05796B',
                              textDecoration: 'underline',
                              fontWeight: 400,
                              fontSize: '14px',
                            }}
                          >
                            Resend Invitation
                          </Button>
                        </HasAccess>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col>
              <HasAccess role={PERMISSIONS.users.user.write}>
                <Button type={'link'} icon={<EditIconButton />} onClick={onUpdateClick} />
              </HasAccess>
            </Col>
          </Row>
          <Row className={'margin-t-4'}>
            <Col span={24}>
              <Row className={'gap-8'} wrap={false} align={'middle'}>
                <Typography.Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {userDetails.formattedCreatedDate}
                </Typography.Text>
                <Typography.Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {userDetails.formattedLastModifiedDate}
                </Typography.Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.additional_info}>
          <Row gutter={[0, 14]}>
            {userDetails.country?.name ? (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  {userDetails.country.flagLocation && <IconWrapper src={userDetails.country.flagLocation} />}
                  <Typography.Text type={'secondary'} className={'font-size-14'}>
                    {userDetails.country.name}
                  </Typography.Text>
                </Row>
              </Col>
            ) : (
              ''
            )}
            <Col span={24}>
              <Row align={'middle'} className={styles.additional_info_item}>
                <Typography.Text type={'secondary'}>Department: {userDetails.department}</Typography.Text>
              </Row>
            </Col>
            {userDetails?.title ? (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <Typography.Text type={'secondary'}>Title: {userDetails.title}</Typography.Text>
                </Row>
              </Col>
            ) : (
              ''
            )}
            {userDetails?.applicationAccess ? (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <Typography.Text type={'secondary'}>
                    Application Access:{' '}
                    {userDetails.applicationAccess
                      .map((i: (typeof USER_TYPES)[keyof typeof USER_TYPES]) => UserTypesMapping[i])
                      .join(', ')}
                  </Typography.Text>
                </Row>
              </Col>
            ) : (
              ''
            )}
            {userDetails?.roleIds?.length ? (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <Col span={24}>
                    <Typography.Text type={'secondary'}>Roles: </Typography.Text>
                  </Col>
                  <Col>
                    {userDetails?.roleIds
                      .filter((_: any, index: number) => index < 9)
                      .map((role: { name: string; id: string }) => (
                        <Typography.Text key={role.id} type={'secondary'} className={styles.role_name}>
                          {role.name}
                        </Typography.Text>
                      ))}
                    {userDetails?.roleIds?.length > 9 ? (
                      <Button
                        size={'small'}
                        type={'text'}
                        variant={'text'}
                        style={{ fontSize: '14px', color: '#525866', backgroundColor: 'transparent', fontWeight: 400 }}
                        onClick={onExpand}
                      >
                        <Typography.Text underline type={'secondary'}>
                          +{userDetails?.roleIds?.length - 9}
                        </Typography.Text>
                      </Button>
                    ) : (
                      ''
                    )}
                  </Col>
                </Row>
              </Col>
            ) : (
              ''
            )}
          </Row>
        </Col>
      </Row>
      <RolesDrawer onExpand={onExpand} drawerState={drawerState} data={userDetails.roleIds} />
    </>
  );
});
